import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { ConsequenceByIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetConsequencesByParentIssueIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC consequence data to match the GraphQL query structure
 */
function mapTrpcConsequencesToGraphQL(
  trpcData: ConsequenceByIdResponseRow[] | undefined
): GetConsequencesByParentIssueIdQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    consequence: trpcData,
  };
}

export const useGetConsequencesByParentIssueId = (
  parentIssueId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.consequence.getByParentIssueId.queryOptions({
      issueId: parentIssueId,
    }),

    enabled: trpcEnabled && !skip && !!parentIssueId,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcConsequencesToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: trpcRefetch,
  };
};
